/**
 * Candle Visitor Tracking Client
 *
 * Fire-and-forget analytics utility. All calls are non-blocking and
 * silently swallow errors — tracking must never impact UX.
 *
 * Architecture: calls the `track-event` Supabase Edge Function which
 * handles geo-IP resolution server-side and inserts into page_views /
 * feature_events tables.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const EDGE_FN_URL = `${SUPABASE_URL}/functions/v1/track-event`;
const SESSION_KEY_STORAGE = 'candle-anon-session-key';
const LEGACY_VIGIL_KEY_STORAGE = 'candle-vigil-visitor-token';
const STRONG_VISITOR_KEY = /^[A-Za-z0-9_-]{32,128}$/;

// ─────────────────────────────────────────────────────────────────────────────
// Session management
// ─────────────────────────────────────────────────────────────────────────────

/** Module-level auth context updated by providers.tsx */
let _currentUserId: string | null = null;
let _currentAccessToken: string | null = null;

/** Generate a cryptographically strong anonymous visitor token. */
function generateAnonKey(): string {
    if (typeof crypto.randomUUID === 'function') {
        return `anon-${crypto.randomUUID().replace(/-/g, '')}`;
    }
    const bytes = crypto.getRandomValues(new Uint8Array(24));
    return `anon-${Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')}`;
}

/** Get or create a persistent anonymous session key */
function getAnonSessionKey(): string {
    let key = localStorage.getItem(SESSION_KEY_STORAGE);
    if (key && STRONG_VISITOR_KEY.test(key)) return key;

    // Preserve existing Vigil identity during the one-time canonical-key migration.
    const legacyVigilKey = localStorage.getItem(LEGACY_VIGIL_KEY_STORAGE);
    key = legacyVigilKey && STRONG_VISITOR_KEY.test(legacyVigilKey)
        ? legacyVigilKey
        : generateAnonKey();
    localStorage.setItem(SESSION_KEY_STORAGE, key);
    return key;
}

/** Stable anonymous identity shared by analytics and anonymous action controls. */
export function getTrackingVisitorKey(): string {
    return getAnonSessionKey();
}

/** Returns the effective session key: user_id for auth users, anon key for guests */
function getSessionKey(): string {
    return _currentUserId ?? getAnonSessionKey();
}

/** Called by the tracking provider when auth state changes */
export function setTrackingUser(userId: string | null, accessToken: string | null = null): void {
    _currentUserId = userId;
    _currentAccessToken = accessToken;
}

// ─────────────────────────────────────────────────────────────────────────────
// Device / browser detection (enhanced — no extra deps)
// ─────────────────────────────────────────────────────────────────────────────

interface DeviceInfo {
    device_type: 'desktop' | 'mobile' | 'tablet';
    device_vendor: string | null;
    device_model: string | null;
    browser: string;
    browser_version: string | null;
    os: string;
    os_version: string | null;
    user_agent: string;
}

function getDeviceInfo(): DeviceInfo {
    const ua = navigator.userAgent;

    // ── OS detection ──────────────────────────────────────────────────────
    let os = 'Unknown';
    let os_version: string | null = null;

    const winMatch = ua.match(/Windows NT ([\d.]+)/i);
    const macMatch = ua.match(/Mac OS X ([\d_]+)/i);
    const androidMatch = ua.match(/Android ([\d.]+)/i);
    const iosMatch = ua.match(/(?:iPhone OS|CPU OS) ([\d_]+)/i);

    if (winMatch) {
        os = 'Windows';
        const ntMap: Record<string, string> = {
            '10.0': '10/11', '6.3': '8.1', '6.2': '8', '6.1': '7', '6.0': 'Vista',
        };
        os_version = ntMap[winMatch[1]] ?? winMatch[1];
    } else if (androidMatch) {
        os = 'Android';
        os_version = androidMatch[1];
    } else if (iosMatch) {
        os = 'iOS';
        os_version = iosMatch[1].replace(/_/g, '.');
    } else if (macMatch) {
        os = 'macOS';
        os_version = macMatch[1].replace(/_/g, '.');
    } else if (/Linux/i.test(ua)) {
        os = 'Linux';
    }

    // ── Browser detection ─────────────────────────────────────────────────
    let browser = 'Unknown';
    let browser_version: string | null = null;

    const edgeMatch = ua.match(/Edg\/([\d.]+)/i);
    const oprMatch = ua.match(/OPR\/([\d.]+)/i) || ua.match(/Opera\/([\d.]+)/i);
    const ffMatch = ua.match(/Firefox\/([\d.]+)/i);
    const chromeMatch = ua.match(/Chrome\/([\d.]+)/i);
    const safariMatch = ua.match(/Version\/([\d.]+).*Safari/i);

    if (edgeMatch) {
        browser = 'Edge';
        browser_version = edgeMatch[1];
    } else if (oprMatch) {
        browser = 'Opera';
        browser_version = oprMatch[1];
    } else if (ffMatch) {
        browser = 'Firefox';
        browser_version = ffMatch[1];
    } else if (chromeMatch && !/Chromium/i.test(ua)) {
        browser = 'Chrome';
        browser_version = chromeMatch[1];
    } else if (safariMatch && !/Chrome/i.test(ua)) {
        browser = 'Safari';
        browser_version = safariMatch[1];
    }

    // ── Device type ───────────────────────────────────────────────────────
    let device_type: 'desktop' | 'mobile' | 'tablet' = 'desktop';
    if (/iPad/i.test(ua) || (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1)) {
        device_type = 'tablet';
    } else if (/Mobi|Android/i.test(ua)) {
        device_type = 'mobile';
    }

    // ── Device vendor / model (best-effort from UA) ───────────────────────
    let device_vendor: string | null = null;
    let device_model: string | null = null;

    if (/iPhone/i.test(ua)) {
        device_vendor = 'Apple'; device_model = 'iPhone';
    } else if (/iPad/i.test(ua)) {
        device_vendor = 'Apple'; device_model = 'iPad';
    } else if (/Macintosh/i.test(ua)) {
        device_vendor = 'Apple'; device_model = 'Mac';
    } else if (androidMatch) {
        const modelMatch = ua.match(/;\s*([^;)]+)\s*Build\//i);
        if (modelMatch) {
            const m = modelMatch[1].trim();
            if (/SM-|samsung/i.test(m)) device_vendor = 'Samsung';
            else if (/Pixel/i.test(m)) device_vendor = 'Google';
            else if (/Moto/i.test(m)) device_vendor = 'Motorola';
            else if (/LM-|LG/i.test(m)) device_vendor = 'LG';
            else if (/HTC/i.test(m)) device_vendor = 'HTC';
            else if (/Huawei/i.test(m)) device_vendor = 'Huawei';
            else if (/Xiaomi|Mi /i.test(m)) device_vendor = 'Xiaomi';
            else if (/OnePlus/i.test(m)) device_vendor = 'OnePlus';
            device_model = m;
        }
    } else if (/Windows/i.test(ua)) {
        device_vendor = 'Microsoft';
        if (/Touch/i.test(ua)) device_model = 'Surface';
    }

    return {
        device_type, device_vendor, device_model,
        browser, browser_version,
        os, os_version,
        user_agent: ua,
    };
}

// ─────────────────────────────────────────────────────────────────────────────
// Network info
// ─────────────────────────────────────────────────────────────────────────────

function getConnectionType(): string | null {
    const nav = navigator as Navigator & {
        connection?: { effectiveType?: string; type?: string };
        mozConnection?: { type?: string };
        webkitConnection?: { type?: string };
    };
    const conn = nav.connection ?? nav.mozConnection ?? nav.webkitConnection;
    if (!conn) return null;
    return (conn as { effectiveType?: string; type?: string }).effectiveType
        ?? (conn as { type?: string }).type
        ?? null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Core HTTP sender
// ─────────────────────────────────────────────────────────────────────────────

interface TrackPayload {
    type: 'page_view' | 'feature_event';
    payload: Record<string, unknown>;
}

function sendTrackEvent(body: TrackPayload): void {
    // Fire-and-forget: no await, no error propagation
    fetch(EDGE_FN_URL, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(_currentAccessToken
                ? { Authorization: `Bearer ${_currentAccessToken}` }
                : {}),
        },
        body: JSON.stringify(body),
        // keepalive ensures the request completes even if page unloads
        keepalive: true,
    }).catch(() => {
        // Silent fail — tracking must never break the app
    });
}

// ─────────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────────

export interface PageViewPayload {
    route: string;
    route_group: string;
    page_id?: string | null;
}

export interface FeatureEventPayload {
    event_name: string;
    event_category: 'engagement' | 'discovery' | 'community' | 'profile' | 'communication';
    page_id?: string | null;
    metadata?: Record<string, unknown>;
}

/**
 * Track a page view. Deduplication (30-min window) is handled server-side
 * in the Edge Function.
 */
export function trackPageView(payload: PageViewPayload): void {
    const device = getDeviceInfo();
    sendTrackEvent({
        type: 'page_view',
        payload: {
            ...payload,
            session_key: getSessionKey(),
            is_authenticated: !!_currentUserId,
            user_id: _currentUserId,
            platform: 'web',
            // Basic device
            device_type: device.device_type,
            browser: device.browser,
            os: device.os,
            // Extended device info
            device_vendor: device.device_vendor,
            device_model: device.device_model,
            browser_version: device.browser_version,
            os_version: device.os_version,
            user_agent: device.user_agent,
            // Network
            connection_type: getConnectionType(),
            referrer: document.referrer || null,
        },
    });
}

/**
 * Track a named feature interaction event.
 */
export function trackFeatureEvent(payload: FeatureEventPayload): void {
    sendTrackEvent({
        type: 'feature_event',
        payload: {
            ...payload,
            session_key: getSessionKey(),
            is_authenticated: !!_currentUserId,
            user_id: _currentUserId,
            platform: 'web',
            metadata: payload.metadata ?? {},
        },
    });
}
